import { useEffect, useMemo } from "react";
import { Button } from "~/components/ui/button";
import { Skeleton } from "~/components/ui/skeleton";
import { Link } from "react-router-dom";
import paths from "~/paths";
import LanguageDisplay from "~/components/LanguageDisplay";
import DocumentPreview from "../DocumentPreview";
import ShareDocumentButton from "~/components/DocumentShareButton";
import DocumentDownloadButton from "~/components/DocumentDownloadButton";
import { toJSON, TranslationInfo, Document } from "~/document";
import DocBookmarkButton from "../DocBookmarkButton";
import languageStore from "~/stores/language";
import { ArrowRight, X } from "lucide-react";
import { observer } from "mobx-react";
import documentStore from "~/stores/document";
import { getFullDocTypes } from "~/util";
import docStore from "~/stores/document";

interface DocumentInfoProps {
  document: Document;
  translations: TranslationInfo[];
}

const DocumentInfo = (props: DocumentInfoProps) => {
  const { translations, document } = props;
  const items = toJSON(document as any);
  const itemsList = [
    { text: "Notes", content: items["Notes"] },
    { text: "Publication Date", content: items["Publication date"] },
    {
      text: "Available languages",
      content: <LanguageDisplay document={document} translations={translations} />,
    },
    { text: "Access", content: document.access_display },
    { text: "Topics", content: items["topics"] },
    { text: "Document types", content: getFullDocTypes(document) },
    { text: "Geographical coverage", content: items["Geographical coverage"] },
    { text: "Document symbol", content: items["Document symbol"] },
    { text: "Document ID", content: items["Document ID"] },
    { text: "Tags", content: items["tags"] },
  ];
  return (
    <div className="flex flex-col justify-start text-sm">
      {itemsList
        .filter(
          (item) =>
            !!item.content &&
            (!(item.content instanceof Array) || item.content.length !== 0) &&
            (!(item.content instanceof Set) || item.content.size !== 0)
        )
        .map((item) => (
          <div className="mb-[18px] text-left last:mb-0" key={item.text}>
            <strong className="block">{item.text}</strong>
            <div className="mt-1.5">{item.content}</div>
          </div>
        ))}
    </div>
  );
};

interface Props {
  document: Document | null;
  loadingDocument: boolean;
  translations: TranslationInfo[];
  onClose: () => void;
}

// Placeholder shaped like the loaded DocumentView (left: title + actions + info
// list + button; right: preview), shown while the selected document loads.
const DocumentViewSkeleton = () => (
  <div className="relative flex h-full min-h-[400px] flex-col overflow-y-auto bg-inherit">
    <div className="flex h-full flex-1 flex-row">
      <div className="mr-6 flex h-full flex-[1_1_50%] flex-col">
        <Skeleton className="mb-2 h-7 w-3/4" />
        <Skeleton className="mb-1 h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <div className="my-4 flex flex-wrap gap-1.5">
          <Skeleton className="h-8 w-24 rounded-md" />
          <Skeleton className="h-8 w-28 rounded-md" />
          <Skeleton className="h-8 w-28 rounded-md" />
        </div>
        <div className="flex flex-col gap-[18px]">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i}>
              <Skeleton className="mb-1.5 h-3 w-28" />
              <Skeleton className="h-4 w-2/3" />
            </div>
          ))}
        </div>
        <Skeleton className="mt-6 h-9 w-40 rounded-md" />
      </div>
      <div className="flex flex-[1_1_50%] flex-col max-[1200px]:hidden">
        <div className="mb-6 flex flex-row-reverse">
          <Skeleton className="size-9 rounded-md" />
        </div>
        <Skeleton className="h-full min-h-[400px] w-full" />
      </div>
    </div>
  </div>
);

const DocumentView = observer((props: Props) => {
  const { translations } = props;
  const doc = props.document;
  const loadingDocument = documentStore.loadingDocumentViewInfo;

  useEffect(() => {
    if (doc === null) {
      return;
    }
    languageStore.loadLanguages();
  }, [doc]);

  const renderedDocument = useMemo(() => {
    if (loadingDocument) {
      return <DocumentViewSkeleton />;
    }

    const canDownload = docStore.canDownload(docStore.currentDocument);

    if (doc === null) return null;
    return (
      <div className="relative flex h-full min-h-[400px] flex-col overflow-y-auto bg-inherit">
        <div className="flex h-full flex-1 flex-row">
          <div className="mr-6 flex h-full flex-[1_1_50%] flex-col">
            <div className="mb-1 flex flex-row items-center">
              <div className="text-left">
                <h3 className="m-0 mb-2 text-[1.375rem] font-semibold leading-tight">{doc.title}</h3>
                {doc.abstract && <div className="text-sm [color:var(--text-02)]">{doc.abstract}</div>}
              </div>
            </div>

            <div className="my-4 flex flex-wrap gap-1.5">
              <ShareDocumentButton doc={doc} />
              {canDownload && (
                <>
                  <DocumentDownloadButton doc={doc} />
                  <DocBookmarkButton doc={doc} />
                </>
              )}
            </div>
            <div>
              <DocumentInfo translations={translations} document={doc} />
            </div>
            <div className="mt-6">
              <Link to={paths.construct.docDetail(doc.protocol)}>
                <Button variant="default">
                  Document details
                  <ArrowRight />
                </Button>
              </Link>
            </div>
          </div>
          <div className="flex flex-[1_1_50%] flex-col max-[1200px]:hidden">
            <div className="mb-6 flex flex-row-reverse">
              <Button variant="ghost" size="icon" aria-label="Close the view" onClick={props.onClose}>
                <X />
              </Button>
            </div>
            <div className="flex-1">
              <DocumentPreview doc={doc} />
            </div>
          </div>
        </div>
      </div>
    );
  }, [doc, loadingDocument]);

  return <div className="h-full w-full flex-1 bg-white">{renderedDocument}</div>;
});

export default DocumentView;
